import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  UserCheck, 
  UserX, 
  X, 
  Shield, 
  Mail, 
  MoreHorizontal, 
  Download, 
  Plus, 
  CheckCircle, 
  XCircle, 
  Trash2, 
  Search, 
  Edit,
  Save,
  Crown,
  Settings,
  AlertCircle,
  Lock,
  Building2
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { 
  getAllCoachingAdminPermissions,
  getCoachingAdminPermissions,
  setCoachingAdminPermissions,
  deleteCoachingAdminPermissions
} from '../../firebase/collections';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import toast from 'react-hot-toast';

// All available coaching admin sections
const ALL_SECTIONS = [
  { id: 'dashboard', name: 'Dashboard', icon: '📊' },
  { id: 'admissions', name: 'Admissions', icon: '🎓' },
  { id: 'tests', name: 'Test Management', icon: '📝' },
  { id: 'exams', name: 'Exam Management', icon: '🛡️' },
  { id: 'resources', name: 'Resource Management', icon: '📁' },
  { id: 'batches', name: 'Batch Management', icon: '👥' },
  { id: 'students', name: 'Student Management', icon: '👨‍🎓' },
  { id: 'teachers', name: 'Teacher Management', icon: '👨‍🏫' },
  { id: 'settlements', name: 'Teacher Settlements', icon: '💰' },
  { id: 'attendance', name: 'Attendance', icon: '✅' },
  { id: 'transport', name: 'Transport Management', icon: '🚌' },
  { id: 'activities', name: 'Activities Management', icon: '🎯' },
  { id: 'grievance', name: 'Grievance Management', icon: '⚠️' },
  { id: 'leave', name: 'Leave Management', icon: '📅' },
  { id: 'fees', name: 'Fee Management', icon: '💳' },
  { id: 'report-cards', name: 'Report Cards', icon: '📄' },
  { id: 'analytics', name: 'Data Analytics', icon: '📈' },
  { id: 'id-cards', name: 'ID Cards', icon: '🪪' },
  { id: 'store', name: 'Store Management', icon: '🛒' },
  { id: 'staff', name: 'Staff Management', icon: '👔' },
  { id: 'subscription', name: 'Subscription', icon: '💎' }
];

const CoachingAdminPermissions = () => {
  const { currentUser, logAdminAction } = useRealtime();
  const [coachingAdmins, setCoachingAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => {
    loadCoachingAdmins();
  }, []);

  const loadCoachingAdmins = async () => {
    try {
      setLoading(true);
      
      // Get all coaching admins
      const adminsQuery = query(
        collection(db, 'users'),
        where('role', '==', 'coaching_admin')
      );
      const adminsSnapshot = await getDocs(adminsQuery);
      const adminsList = [];
      adminsSnapshot.forEach((doc) => {
        adminsList.push({ id: doc.id, ...doc.data() });
      });

      // Load permissions for each admin
      const adminsWithPermissions = await Promise.all(
        adminsList.map(async (admin) => {
          const permResult = await getCoachingAdminPermissions(admin.email);
          return {
            ...admin,
            permissions: permResult.success && permResult.data ? permResult.data : null
          };
        })
      );

      setCoachingAdmins(adminsWithPermissions);
    } catch (error) {
      toast.error('Failed to load coaching admins: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPermissions = (admin) => {
    setEditingAdmin(admin);
    const currentPerms = admin.permissions?.permissions || [];
    setPermissions({
      allowedSections: [...currentPerms],
      isActive: admin.permissions?.isActive !== false,
      notes: admin.permissions?.notes || ''
    });
  };

  const handleToggleSection = (sectionId) => {
    setPermissions(prev => {
      const sections = prev.allowedSections || [];
      if (sections.includes(sectionId)) {
        return {
          ...prev,
          allowedSections: sections.filter(id => id !== sectionId)
        };
      } else {
        return {
          ...prev,
          allowedSections: [...sections, sectionId]
        };
      }
    });
  };

  const handleSavePermissions = async () => {
    if (!editingAdmin) return;

    setSaving(true);
    try {
      const result = await setCoachingAdminPermissions(editingAdmin.email, {
        ...permissions,
        createdBy: currentUser.email
      });

      if (result.success) {
        toast.success('Permissions updated successfully');
        await logAdminAction('update_coaching_admin_permissions', {
          adminEmail: editingAdmin.email,
          permissions: permissions.allowedSections
        });
        setEditingAdmin(null);
        loadCoachingAdmins();
      } else {
        toast.error('Failed to update permissions: ' + result.error);
      }
    } catch (error) {
      toast.error('Error updating permissions: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePermissions = async (adminEmail) => {
    try {
      const result = await deleteCoachingAdminPermissions(adminEmail);
      if (result.success) {
        toast.success('Permissions removed - admin now has full access');
        await logAdminAction('delete_coaching_admin_permissions', { adminEmail });
        loadCoachingAdmins();
      } else {
        toast.error('Failed to remove permissions: ' + result.error);
      }
    } catch (error) {
      toast.error('Error removing permissions: ' + error.message);
    }
    setShowDeleteConfirm(null);
  };

  const filteredAdmins = coachingAdmins.filter(admin =>
    admin.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    admin.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    admin.coachingName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <Building2 className="mr-3 text-primary-600" size={32} />
          Coaching Admin Permissions
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Manage section access for coaching administrators
        </p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search by name, email, or coaching name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none"
          />
        </div>
      </div>

      {/* Admins List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredAdmins.map((admin) => (
            <div
              key={admin.id}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="h-12 w-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 font-bold text-lg">
                    {admin.displayName?.[0]?.toUpperCase() || admin.email?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {admin.displayName || 'Unnamed Admin'}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center mt-1">
                      <Mail size={14} className="mr-1" />
                      {admin.email}
                    </p>
                    {admin.coachingName && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center mt-1">
                        <Building2 size={14} className="mr-1" />
                        {admin.coachingName}
                      </p>
                    )}
                    <div className="mt-2">
                      {admin.permissions ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                          <Lock size={12} className="mr-1" />
                          {admin.permissions.permissions?.length || 0} sections allowed
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                          <CheckCircle size={12} className="mr-1" />
                          Full Access
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditPermissions(admin)}
                    className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                    title="Edit Permissions"
                  >
                    <Edit size={18} />
                  </button>
                  {admin.permissions && (
                    <button
                      onClick={() => setShowDeleteConfirm(admin.email)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      title="Remove Restrictions"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {filteredAdmins.length === 0 && (
            <div className="text-center py-12">
              <UserX className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-600 dark:text-gray-400">No coaching admins found</p>
            </div>
          )}
        </div>
      )}

      {/* Edit Permissions Modal */}
      {editingAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
                <Shield className="mr-2 text-primary-600" size={24} />
                Edit Permissions: {editingAdmin.displayName}
              </h2>
              <button
                onClick={() => setEditingAdmin(null)}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="mb-6">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={permissions.isActive}
                    onChange={(e) => setPermissions(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Active (uncheck to temporarily disable access)
                  </span>
                </label>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Allowed Sections ({permissions.allowedSections?.length || 0} selected)
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {ALL_SECTIONS.map((section) => {
                    const isSelected = permissions.allowedSections?.includes(section.id);
                    return (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => handleToggleSection(section.id)}
                        className={`flex items-center justify-between px-4 py-3 rounded-lg border text-sm transition-all ${
                          isSelected
                            ? 'bg-primary-50 border-primary-500 text-primary-700 dark:bg-primary-900/20 dark:border-primary-500 dark:text-primary-400'
                            : 'bg-white border-gray-200 text-gray-600 hover:border-primary-300 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400'
                        }`}
                      >
                        <span className="flex items-center">
                          <span className="mr-2">{section.icon}</span>
                          {section.name}
                        </span>
                        {isSelected && <CheckCircle size={16} />}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notes (optional)
                </label>
                <textarea
                  value={permissions.notes}
                  onChange={(e) => setPermissions(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none"
                  placeholder="Add any notes about these permissions..."
                />
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg flex items-start space-x-3">
                <AlertCircle className="text-blue-600 dark:text-blue-400 mt-0.5" size={20} />
                <div className="text-sm text-blue-700 dark:text-blue-300">
                  <p className="font-semibold mb-1">Permission Hierarchy</p>
                  <p>Coaching admins can only grant their staff access to sections they themselves have permission to access.</p>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex space-x-4">
              <button
                onClick={() => setEditingAdmin(null)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePermissions}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    <span>Save Permissions</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              Remove Permission Restrictions?
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              This will remove all permission restrictions for this coaching admin, granting them full access to all sections.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeletePermissions(showDeleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Remove Restrictions
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachingAdminPermissions;
